import { Core, UI, WebViewerInstance } from '@pdftron/webviewer';
import { ThunkDispatch } from '@reduxjs/toolkit';

import { API } from '../../../../../service/drawings/src/ui/common';
import { setterDrawings } from '../../../../../service/drawings/src/ui/store/drawings-slice';
import { DrawingSheetOrDocumentIdDataType, SheetDataType } from '../../../../../service/drawings/src/ui/types/types';
import { getAxios } from '../get-axios';

const SCREENSHOT_SIZE = 80;
const QUALITY_MULTIPLIER = 4;
const PAGE_NUMBER = 1;
const RADAR_RADIUS = 30;

const normalizeCoordinates = (
    coords: { x: number; y: number },
    pageNumber: number,
    documentViewer: Core.DocumentViewer,
) => {
    const pageInfo = documentViewer.getDocument().getPageInfo(pageNumber);
    const rotation = documentViewer.getDocument().getPageRotation(pageNumber);

    let normalizedX = coords.x;
    let normalizedY = coords.y;

    switch (rotation) {
        case 0:
            break;

        case 270:
            normalizedX = coords.y;
            normalizedY = pageInfo.height - coords.x;
            break;

        case 90:
        case 180:
        default:
            console.error('Unknown rotation:', rotation);
    }

    return {
        x: normalizedX,
        y: normalizedY,
    };
};

const storeLink: { [key: string]: SheetDataType[] } = {};

async function fetchSheetLinks(sheetId: DrawingSheetOrDocumentIdDataType, force = false): Promise<void> {
    if (!force && storeLink[sheetId.sheetId!]) return;

    try {
        const response = await getAxios().get(
            `${API.DRAWINGS_S3_URL}/get-sheet-links?drawingSetId=${sheetId.drawingSetId}&sheetId=${sheetId.sheetId}`,
            {
                headers: {
                    Authorization: `Bearer ${API.GRAPHQL_TOKEN}`,
                },
            },
        );
        const links = (response.data as SheetDataType[]).map((link) => ({
            ...link,
            drawingSetId: sheetId.drawingSetId,
        }));
        storeLink[sheetId.sheetId!] = links;
    } catch (error) {
        console.error('Error getting sheet links:', error);
    }
}

export const ViewerAppryzeNavigationTool = (
    instance: WebViewerInstance,
    dispatch: ThunkDispatch<any, any, any>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    sheetId: DrawingSheetOrDocumentIdDataType,
    onError: (error: string) => void,
) => {
    const docViewer = instance.Core.documentViewer;

    const onDocumentLoaded = async () => {
        try {
            if (!storeLink[sheetId.sheetId!]) {
                await fetchSheetLinks(sheetId, true);
            }
        } catch (e) {
            console.error(e);
            onError('Failed to load links for the sheet');
        }
    };

    docViewer.addEventListener('documentLoaded', onDocumentLoaded);

    class ScreenshotCreateTool extends instance.Core.Tools.GenericAnnotationCreateTool {
        constructor(documentViewer: any) {
            const DummyAnnotation = class extends instance.Core.Annotations.RectangleAnnotation {
                constructor() {
                    super();
                    this.Subject = 'Screenshot';
                }
            };

            super(documentViewer, DummyAnnotation as any);
            this.cursor = 'pointer';
        }

        getNearSheetData(x: number, y: number) {
            const [nearSheetData] =
                storeLink[sheetId.sheetId!]?.filter((link) => {
                    const dx = link.x - x;
                    const dy = link.y - y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    return distance < RADAR_RADIUS;
                }) || [];
            return nearSheetData;
        }

        async mouseLeftDown(e: any) {
            setLoading(true);

            const coords = normalizeCoordinates(
                instance.Core.documentViewer.getViewerCoordinatesFromMouseEvent(e),
                PAGE_NUMBER,
                instance.Core.documentViewer,
            );

            const halfSize = SCREENSHOT_SIZE / 2;
            const x = coords.x - halfSize;
            const y = coords.y - halfSize;

            const nearSheetData = this.getNearSheetData(x, y);

            if (nearSheetData) {
                // Ensure drawingSetId is always present
                const goToSheet = {
                    ...nearSheetData,
                    drawingSetId: nearSheetData.drawingSetId || sheetId.drawingSetId,
                };
                dispatch(setterDrawings({ goToSheet }));
                setLoading(false);
            } else {
                await this.captureWithApryseAPI(x, y, SCREENSHOT_SIZE, SCREENSHOT_SIZE);
            }
        }

        async captureWithApryseAPI(x: number, y: number, width: number, height: number) {
            try {
                const doc = instance.Core.documentViewer.getDocument();

                if (!doc) {
                    console.error('No document loaded');
                    return;
                }

                doc.loadCanvas({
                    pageNumber: 1,
                    zoom: 2,
                    drawComplete: (pageCanvas) => {
                        const canvas = document.createElement('canvas');
                        canvas.width = width * QUALITY_MULTIPLIER;
                        canvas.height = height * QUALITY_MULTIPLIER;
                        const ctx = canvas.getContext('2d');

                        ctx?.drawImage(
                            pageCanvas,
                            x * 4,
                            y * 4,
                            SCREENSHOT_SIZE * 4,
                            SCREENSHOT_SIZE * 4,
                            0,
                            0,
                            width * QUALITY_MULTIPLIER,
                            height * QUALITY_MULTIPLIER,
                        );

                        canvas.toBlob(async (blob) => {
                            if (!blob) return;
                            const formData = new FormData();
                            formData.append('image', blob);
                            formData.append('drawingSetId', sheetId.drawingSetId!);
                            formData.append('sheetId', sheetId.sheetId!);
                            formData.append('x', `${x}`);
                            formData.append('y', `${y}`);

                            try {
                                const response = await getAxios().post(
                                    `${API.DRAWINGS_S3_URL}/extract-designations`,
                                    formData,
                                    {
                                        headers: {
                                            'Content-Type': 'multipart/form-data',
                                            Authorization: `Bearer ${API.GRAPHQL_TOKEN}`,
                                        },
                                    },
                                );

                                const [sheetData] = response.data as SheetDataType[];

                                if (!sheetData) {
                                    onError?.(
                                        'Unfortunately, the area you clicked does not contain a link to navigate to another sheet.',
                                    );
                                }

                                const goToSheet = { ...sheetData, x, y, drawingSetId: sheetId.drawingSetId };

                                if (!storeLink[sheetId.sheetId!]) {
                                    storeLink[sheetId.sheetId!] = [];
                                }

                                storeLink[sheetId.sheetId!].push(goToSheet);
                                dispatch(setterDrawings({ goToSheet }));

                                if (response.status !== 200) {
                                    throw new Error('Failed to upload screenshot');
                                }
                            } catch (error) {
                                console.error('Error uploading screenshot:', error);
                            }
                        }, 'image/png');
                    },
                });
            } catch (error) {
                console.error('Error in captureWithApryseAPI:', error);
            }
        }

        mouseLeftUp(e: any) {
            console.log('222222222', e);
        }
    }

    const screenshotToolName = 'AnnotationCreateScreenshot';
    const screenshotTool = new ScreenshotCreateTool(instance.Core.documentViewer);

    instance.UI.registerTool({
        toolName: screenshotToolName,
        toolObject: screenshotTool as any,
        buttonImage: 'ic_snipping_black_24px',
        buttonName: 'screenshotToolButton',
        tooltip: 'Screenshot Tool - Click to capture 100x100 area',
    });

    instance.UI.setHeaderItems((header: UI.Header) => {
        try {
            header.getHeader('toolbarGroup-Shapes').get('freeHandToolGroupButton').insertBefore({
                type: 'toolButton',
                toolName: screenshotToolName,
            });
        } catch (error) {
            console.warn('Could not add screenshot tool to Shapes group:', error);
        }
    });
};
